import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import moment from 'moment-timezone';
import Pagination from 'components/Pagination';
import Footer from '../Footer';
import { Alert, Intent } from '@blueprintjs/core';
import Toast from 'helpers/toaster';
import qs from 'qs';
import { formatTimeOnly, formatDateOnly } from 'helpers/time';
import { currencyFormattedAmount } from 'helpers/currency';
import * as DataList from 'components/DataList';

// Column and card layouts, shared with the page's loading skeleton.
export const QUOTE_COLUMNS = [
  { area: 'estimate', label: 'Estimate #', width: '150px', bone: 'short' },
  { area: 'job', label: 'Job #', width: '130px', bone: 'short', icon: 'clipboard' },
  { area: 'client', label: 'Customer', width: 'minmax(180px, 1.4fr)', icon: 'map-marker' },
  { area: 'description', label: 'Description', width: 'minmax(160px, 1.2fr)' },
  { area: 'created_by', label: 'Created by', width: 'minmax(150px, 1fr)', icon: 'person' },
  { area: 'total', label: 'Grand total', width: '120px', bone: 'short' },
  { area: 'status', label: 'Status', width: '112px', bone: 'pill' },
  { area: 'actions', label: '', width: '64px', bone: 'none' },
];

export const QUOTE_CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'estimate status actions',
    'description description description',
    'client client client',
    'job job job',
    'created_by total total',
  ],
  footer: ['created_by', 'total'],
};

export const QUOTE_MIN_TABLE_WIDTH = 1180;

// Estimate status -> pill tone (values from the Estimates status filter).
const STATUS_TONES = {
  draft: 'grey',
  submitted: 'blue',
  sent: 'blue',
  approved: 'green',
  on_hold: 'amber',
  rejected: 'red',
};

const titleize = (value) =>
  String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

// Descriptions are stored as HTML; the list shows plain text.
const htmlToText = (html) => {
  if (!html) return '';
  const node = document.createElement('div');
  node.innerHTML = html;
  return (node.textContent || '').replace(/\s+/g, ' ').trim();
};

const QUOTE_FILTERS = {
  status: 'status',
  event_number: 'job number',
  service_type_id: 'service type',
  created_by_id: 'created by',
  client_id: 'customer',
  location_id: 'location',
};

@inject('store', 'quoteStore', 'viewStore', 'clientStore', 'userStore') @observer
class QuotesTable extends Component {

  @observable isDeleteDialogVisible = false;
  @observable quote;

  userIsTechnician = this.props.viewStore.currentUser?.attributes?.role === 'technician';

  navigate() {
    if (this.userIsTechnician) {
      this.props.viewStore.technicianQuotes();
    } else {
      this.props.viewStore.quotes();
    }
  }

  onPaginationChange(newPage) {
    let {
      viewStore: {
        quoteSearch
      }
    } = this.props;
    this.props.viewStore.quoteSearch = {
      ...quoteSearch,
      page: newPage
    };
    this.navigate();
  }

  // Keep the date range (and a technician's own-estimates scope), drop the rest.
  clearFilters() {
    const { viewStore } = this.props;
    const { where } = viewStore.quoteSearch;
    viewStore.quoteSearch = {
      ...viewStore.quoteSearch,
      where: {
        created_at: where.created_at,
        kind: where.kind,
        event_number: null,
        grand_total: { gte: null, lte: null },
        ...(this.userIsTechnician ? { created_by_id: where.created_by_id } : {}),
      },
      page: 1,
    };
    viewStore.bumpListFilters();
    this.navigate();
  }

  widenDateRange() {
    const { viewStore } = this.props;
    const { where } = viewStore.quoteSearch;
    const start = moment.unix(parseInt(where.created_at.gte, 10));
    viewStore.quoteSearch = {
      ...viewStore.quoteSearch,
      where: {
        ...where,
        created_at: { ...where.created_at, gte: start.subtract(3, 'months').startOf('day').unix() },
      },
      page: 1,
    };
    viewStore.bumpListFilters();
    this.navigate();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.quoteStore.deleteQuote(this.quote)
      .then(this.onSuccessfulDeleteQuote.bind(this))
      .catch(this.onFailedQuoteDelete.bind(this))
  }

  onSuccessfulDeleteQuote(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Quote deleted successfully!', intent: Intent.SUCCESS })
    this.quote = null;
    this.isDeleteDialogVisible = false;
  }

  onFailedQuoteDelete(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete quote!', intent: Intent.DANGER })
  }

  handleQuoteClick(event, quote) {
    switch (event.label) {
      case 'Edit':
        if (this.userIsTechnician) {
          this.props.viewStore.showTechnicianQuote({ uuid: quote.attributes.id });
        } else {
          this.props.viewStore.ShowQuote({ uuid: quote.attributes.id });
        }
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.quote = quote;
        break;
      default:
    }
  }

  renderEmpty() {
    const { quoteSearch } = this.props.viewStore;
    const where = quoteSearch.where || {};
    const names = this.userIsTechnician
      ? Object.fromEntries(Object.entries(QUOTE_FILTERS).filter(([key]) => key !== 'created_by_id'))
      : QUOTE_FILTERS;
    const filters = DataList.activeFilters(where, names);
    const total = where.grand_total || {};
    if (total.gte || total.lte) filters.push('grand total');
    return (
      <DataList.Empty
        noun="estimates"
        filters={filters}
        onClearFilters={() => this.clearFilters()}
        extraAction={where.created_at && where.created_at.gte
          ? { label: 'Include 3 more months', icon: 'history', onClick: () => this.widenDateRange() }
          : null}
      />
    );
  }

  render() {
    let {
      viewStore,
      viewStore: {
        quoteSearch,
        jobTab: {
          section
        }
      },
      quoteStore: {
        quotes: {
          objects,
          meta
        }
      }
    } = this.props;
    if (objects.length === 0) {
      return this.renderEmpty();
    }
    const technician = this.userIsTechnician;
    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group responsive-pagination bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={QUOTE_COLUMNS}
          cards={QUOTE_CARDS}
          minTableWidth={QUOTE_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="estimates"
          note={DataList.sortNote(quoteSearch.order)}
        >
          {objects.map((quote) => {
            const attributes = quote.attributes;
            const job = attributes.job || {};
            const client = attributes.client || {};
            const createdBy = attributes.created_by || {};
            const description = htmlToText(attributes.description);
            const showLink = technician ? `/technician/quotes/${quote.id}/show` : `/dashboard/quotes/${quote.id}/show`;
            return (
              <DataList.Row key={quote.id}>
                <DataList.Cell area="estimate">
                  <div className="dl-lines">
                    <DataList.RowLink href={showLink} label={`Estimate ${attributes.number}`}>
                      #{attributes.number}
                    </DataList.RowLink>
                    <div className="dl-muted dl-truncate">
                      {formatDateOnly(attributes.created_at)}, {formatTimeOnly(attributes.created_at)}
                    </div>
                  </div>
                </DataList.Cell>
                <DataList.Cell area="job">
                  <DataList.Lines
                    primary={job.event_number ? <React.Fragment><DataList.CardOnly>Job </DataList.CardOnly>#{job.event_number}</React.Fragment> : '—'}
                    title={job.event_number ? `Job #${job.event_number}` : undefined}
                    href={!technician && job.id ? `/dashboard/jobs/${job.id}/${section}` : undefined}
                    secondary={job.service_type_name}
                  />
                </DataList.Cell>
                <DataList.Cell area="client">
                  <DataList.Lines
                    primary={client.name}
                    href={!technician && client.id
                      ? `/dashboard/clients/${client.id}?${qs.stringify(viewStore.clientShowSearch, { encode: false })}`
                      : undefined}
                    secondary={attributes.location && attributes.location.full_address}
                  />
                </DataList.Cell>
                <DataList.Cell area="description">
                  <DataList.Lines
                    primary={description || <span className="dl-muted">No description</span>}
                    title={description || undefined}
                    strong={false}
                    secondary={attributes.po_number ? `PO ${attributes.po_number}` : null}
                  />
                </DataList.Cell>
                <DataList.Cell area="created_by">
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>Created by </DataList.CardOnly>{createdBy.full_name}</React.Fragment>}
                    title={createdBy.full_name}
                    href={!technician && createdBy.id
                      ? `/dashboard/users/${createdBy.id}/show?${qs.stringify(viewStore.userShowSearch, { encode: false })}`
                      : undefined}
                    secondary={createdBy.email}
                  />
                </DataList.Cell>
                <DataList.Cell area="total">
                  <DataList.Lines primary={currencyFormattedAmount(attributes.grand_total)} />
                </DataList.Cell>
                <DataList.Cell area="status">
                  {attributes.status && (
                    <DataList.Pill tone={STATUS_TONES[attributes.status] || 'grey'}>
                      {titleize(attributes.status)}
                    </DataList.Pill>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit estimate', onClick: () => this.handleQuoteClick({ label: 'Edit' }, quote) },
                      { icon: 'trash', label: 'Delete estimate', intent: 'danger', onClick: () => this.handleQuoteClick({ label: 'Delete' }, quote) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            All associated data of this quote will also be deleted. Are you sure you want to do this?
          </p>
        </Alert>
        <Footer>
          <div className="bp3-navbar-group responsive-pagination bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
      </React.Fragment>
    )
  }
}

export default QuotesTable;
